import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { z } from "zod";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon } from "lucide-react";

const searchSchema = z.object({ q: z.string().optional().default("") });

export const Route = createFileRoute("/search")({
  component: SearchPage,
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Search — CoderLity" },
      { name: "description", content: "Search across CoderLity services, software, hosting, blog, portfolio and support articles." },
      { property: "og:url", content: "/search" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/search" }],
  }),
});

const index: { title: string; to: string; category: string; desc: string }[] = [
  { title: "Web Development", to: "/services/web-development", category: "Services", desc: "React, Next.js, Laravel and headless websites." },
  { title: "Mobile Apps", to: "/services/mobile-application", category: "Services", desc: "iOS, Android and React Native apps." },
  { title: "SaaS Development", to: "/services/saas-development", category: "Services", desc: "Multi-tenant SaaS products." },
  { title: "AI Automation", to: "/services/ai-automation", category: "Services", desc: "LLM workflows and AI agents." },
  { title: "UI / UX Design", to: "/services/ui-ux-design", category: "Services", desc: "Product design and design systems." },
  { title: "E-commerce", to: "/services/ecommerce", category: "Services", desc: "Shopify, WooCommerce, headless." },
  { title: "DevOps", to: "/services/devops", category: "Services", desc: "Cloud infra, CI/CD, observability." },
  { title: "API Integration", to: "/services/api-integration", category: "Services", desc: "Connect any system to any system." },
  { title: "Maintenance", to: "/services/maintenance", category: "Services", desc: "Ongoing website and software care." },
  { title: "SEO Services", to: "/marketing/search-seo", category: "Marketing", desc: "Rank higher on Google." },
  { title: "Local SEO", to: "/marketing/local-seo", category: "Marketing", desc: "Google Business Profile optimisation." },
  { title: "Google Ads & PPC", to: "/marketing/paid-advertising", category: "Marketing", desc: "Paid ads on Google, Meta, TikTok." },
  { title: "Social Media Marketing", to: "/marketing/social-content", category: "Marketing", desc: "Organic and paid social." },
  { title: "Email Marketing", to: "/marketing/email-automation", category: "Marketing", desc: "Flows and campaigns." },
  { title: "WhatsApp & SMS", to: "/marketing/whatsapp-sms-marketing", category: "Marketing", desc: "Broadcast and 2-way messaging." },
  { title: "CRO", to: "/marketing/conversion-optimization", category: "Marketing", desc: "A/B testing and funnel work." },
  { title: "Analytics & Tracking", to: "/marketing/analytics-tracking", category: "Marketing", desc: "GA4, GTM, server-side tracking." },
  { title: "Branding & Creative", to: "/marketing/branding-creative-design", category: "Marketing", desc: "Identity and ad creative." },
  { title: "Content Marketing", to: "/marketing/content-marketing", category: "Marketing", desc: "SEO content and thought leadership." },
  { title: "ERP", to: "/software/erp", category: "Software", desc: "Enterprise resource planning." },
  { title: "POS", to: "/software/pos", category: "Software", desc: "Point-of-sale for retail and F&B." },
  { title: "CRM", to: "/software/crm", category: "Software", desc: "Sales, support and marketing." },
  { title: "HRM & Payroll", to: "/software/hrm-payroll", category: "Software", desc: "People ops end-to-end." },
  { title: "LMS", to: "/software/lms", category: "Software", desc: "Sell courses and certify learners." },
  { title: "Accounting", to: "/software/accounting", category: "Software", desc: "Invoicing, tax, reporting." },
  { title: "Inventory", to: "/software/inventory", category: "Software", desc: "Stock, warehouses, POs." },
  { title: "Booking", to: "/software/booking", category: "Software", desc: "Appointments and resources." },
  { title: "Marketplace", to: "/software/marketplace", category: "Software", desc: "Multi-vendor platforms." },
  { title: "Web Hosting", to: "/hosting/web", category: "Hosting", desc: "Fast NVMe SSD hosting." },
  { title: "VPS & Cloud", to: "/hosting/vps-cloud", category: "Hosting", desc: "Managed and unmanaged VPS." },
  { title: "Dedicated Servers", to: "/hosting/dedicated-servers", category: "Hosting", desc: "Bare-metal servers." },
  { title: "Domains", to: "/hosting/domains", category: "Hosting", desc: "Register any TLD." },
  { title: "SSL Certificates", to: "/hosting/ssl", category: "Hosting", desc: "Free and premium SSL." },
  { title: "WHOIS Lookup", to: "/hosting/whois", category: "Hosting", desc: "Domain ownership lookup." },
  { title: "Portfolio", to: "/portfolio", category: "Company", desc: "Case studies and past work." },
  { title: "Blog", to: "/blog", category: "Company", desc: "Guides and insights." },
  { title: "Support", to: "/support", category: "Company", desc: "Help center and tickets." },
  { title: "Contact", to: "/contact", category: "Company", desc: "Talk to our team." },
  { title: "Pricing", to: "/pricing", category: "Company", desc: "Plans and pricing." },
  { title: "Careers", to: "/careers", category: "Company", desc: "Join our remote team." },
];

function SearchPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const [term, setTerm] = useState(q);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return index.filter((r) =>
      r.title.toLowerCase().includes(needle) ||
      r.desc.toLowerCase().includes(needle) ||
      r.category.toLowerCase().includes(needle)
    );
  }, [q]);

  return (
    <PageShell>
      <PageHero eyebrow="Search" title="Find what you need" sub="Services, software, hosting, blog, portfolio and support articles." />
      <section className="py-8">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              navigate({ to: "/search", search: { q: term } });
            }}
            className="relative"
          >
            <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search anything…" className="h-14 pl-12 text-base" />
          </form>
        </div>
      </section>
      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          {q ? (
            <>
              <p className="text-sm text-muted-foreground">{results.length} results for "{q}"</p>
              <div className="mt-6 space-y-3">
                {results.map((r) => (
                  <Link key={r.to} to={r.to} className="block rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition">
                    <div className="text-xs font-semibold uppercase tracking-widest text-primary">{r.category}</div>
                    <div className="mt-1 text-lg font-semibold">{r.title}</div>
                    <div className="text-sm text-muted-foreground">{r.desc}</div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-center">Type a query above to search.</p>
          )}
        </div>
      </section>
    </PageShell>
  );
}
